// ── The presentation script ──────────────────────────────────────────────────
// The synced sales deck. The presenter console shows `notes` (what to say /
// show on the projector); every attendee's deck bar shows `title` + `hint`
// (what THEY should look at or try on their own device). Scenes are ordered;
// the presenter advances them and all devices follow.
//
// Every scene carries a `part_key`: the platform part it demonstrates — the
// client is buying PARTS, so every step names its part. Two pseudo-keys drive
// the commercial close: 'numbers' (the forecast) and 'assembled' (the van
// builder). Scene→admin-page, forecast and van triggers all key off part_key —
// never off title text, so renaming a scene can't break the wiring.
//
// The live deck is admin-authored in demo_scene_defs (Product Demos page);
// DEMO_SCENES below is the built-in fallback used when that table is empty or
// unreachable, and the seed the migration copied verbatim.
import { supabase } from '@/lib/supabaseClient';
import { FIELD_ASSIST_ENABLED } from '@/lib/featureFlags';

// Part chip shown on the attendee scene card: what they're using, as a product.
export const PART_META = {
  'core-portal':  { name: 'Core Portal',              van: 'Chassis',     price: 12 },
  operations:     { name: 'Operations Engine',        van: 'Engine',      price: 8 },
  'routes-pay':   { name: 'Routes → Pay',             van: 'Drivetrain',  price: 5 },
  invoicing:      { name: 'Invoicing Engine',         van: 'Fuel gauge',  price: 6 },
  performance:    { name: 'Performance Intelligence', van: 'Dashboard',   price: 5 },
  'my-day':       { name: 'My Day / Calendar',        van: 'Sat-nav',     price: 4 },
  'field-assist': { name: 'Field Assist',             van: 'Toolbox',     price: 4 },
  'field-cases':  { name: 'Field Cases',              van: 'Glovebox',    price: 4 },
  recruitment:    { name: 'Recruitment & Onboarding', van: 'Crew seats',  price: 5 },
  accounting:     { name: 'Accounting Bridge',        van: 'Logbook',     price: 6 },
  terminations:   { name: 'Terminations Desk',        van: 'Handbrake',   price: 3 },
  // SDK-act parts (3 Oct). Prices are PLACEHOLDERS mirroring 20261259 —
  // the owner sets the real figure in Parts and these chips follow.
  'whatsapp-agent': { name: 'WhatsApp Agent',          van: 'Two-way radio',  price: 6 },
  'faq-assistant':  { name: 'FAQ Assistant',           van: "Owner's manual", price: 3 },
};

// ── Live part prices (4 Oct) ─────────────────────────────────────────────────
// The chip figures above are OFFLINE FALLBACKS. The truth is
// platform_parts.price_per_crew, edited by the admin in Product Demos →
// Parts and read here through the part_prices() RPC (20261260 — SECURITY
// DEFINER, because the attendee deck bar is not admin). loadPartPrices()
// patches PART_META in place, once per window; the useLivePartPrices hook
// (src/hooks/useLivePartPrices.js) re-renders a chip-bearing component when
// the patch lands. A failed fetch keeps the fallbacks — never a blank chip.
let PRICE_PROMISE = null;
export const loadPartPrices = () => {
  PRICE_PROMISE ||= supabase
    .rpc('part_prices')
    .then(({ data }) => {
      for (const r of data || []) {
        const n = Number(r.price_per_crew);
        if (PART_META[r.key] && Number.isFinite(n)) {
          PART_META[r.key] = { ...PART_META[r.key], price: n };
        }
      }
      return true;
    })
    .catch(() => { PRICE_PROMISE = null; return false; });
  return PRICE_PROMISE;
};

// The admin page that PROVES each part live — the presenter's "Show on screen"
// pills. One source of truth; both the console and any future surface use it.
export const PART_ADMIN_LINKS = {
  'core-portal':  [{ label: 'Directory & messages', path: '/admin/dashboard/subcontractors' }],
  operations:     [
    { label: 'Allocations board', path: '/admin/dashboard/allocations' },
    { label: 'Uniforms desk', path: '/admin/dashboard/uniforms' },
  ],
  'routes-pay':   [{ label: 'Route logs', path: '/admin/dashboard/logging' }],
  invoicing:      [{ label: 'Invoicing', path: '/admin/dashboard/invoicing' }],
  performance:    [{ label: 'Performance hub', path: '/admin/dashboard/performance' }],
  'my-day':       [{ label: 'My Day admin', path: '/admin/dashboard/protocols' }],
  // The Field Intelligence route only EXISTS when the flag is on — linking to
  // it dark sent the presenter to a dead route mid-demo. No page → the scene
  // stays on the curtain, which is the owner's rule for uncovered scenes.
  'field-assist': FIELD_ASSIST_ENABLED ? [{ label: 'Field intelligence', path: '/admin/dashboard/field-intelligence' }] : [],
  'field-cases':  [{ label: 'Field cases', path: '/admin/dashboard/field-cases' }],
  recruitment:    [{ label: 'Recruitment hub', path: '/admin/dashboard/recruitment' }],
  accounting:     [{ label: 'Accounting', path: '/admin/dashboard/accounting' }],
  terminations:   [{ label: 'Terminations desk', path: '/admin/dashboard/terminations' }],
  // Both prove on the live WhatsApp inbox (the FAQ brain answers in-thread).
  'whatsapp-agent': [{ label: 'WhatsApp inbox', path: '/admin/dashboard/recruitment?tab=rescue&section=whatsapp' }],
  'faq-assistant':  [{ label: 'WhatsApp inbox', path: '/admin/dashboard/recruitment?tab=rescue&section=whatsapp' }],
  // ?showreel=1 auto-plays the full assembly on arrival — the closing scene
  // should show the van BUILDING, not already built. Consumers comparing the
  // current location against a link must compare pathname only (see samePath).
  assembled:      [{ label: 'Van lab', path: '/admin/dashboard/business-van-lab?showreel=1' }],
};

// Where "Explore it" drops the attendee, per part — the guided half of the
// deck: the presenter pushes a scene, the takeover pitches the part, and the
// explore button lands the attendee INSIDE that module to move around freely.
export const PART_SUB_ROUTES = {
  'core-portal':  '/subcontractor/dashboard',
  operations:     '/subcontractor/dashboard/availability',
  'routes-pay':   '/subcontractor/dashboard/logs',
  invoicing:      '/subcontractor/dashboard/live-invoice',
  performance:    '/subcontractor/dashboard/performance',
  'my-day':       '/subcontractor/dashboard/my-day',
  'field-assist': '/subcontractor/dashboard/field-assist',
  'field-cases':  '/subcontractor/dashboard/cases',
};

// The big line the takeover throws, per part. Eyes-up scenes use their own
// title instead (two scenes can share a part; only the hands-on one punches).
// Owner rules: specific remedies for how the work is done manually today —
// no banter, no jargon (22 Sep); and SHORT — the fewer the words the harder
// it lands on a phone screen, the detail belongs in the bullets (22 Sep).
export const PART_PUNCHLINES = {
  'core-portal':  'Every crew, contract and message. One system.',
  operations:     'The week submitted in seconds. Nobody chases.',
  'routes-pay':   'The route file becomes the day’s hours. Nobody types.',
  invoicing:      'Routes in. Finished invoice out.',
  performance:    'Grading emails become standings and coaching.',
  'my-day':       'Route start in. Wake, leave, arrive out.',
  'field-assist': 'Point the phone. Governed answers, spoken back.',
  'field-cases':  'Evidence filed at the kerb. Replies reviewed.',
  recruitment:    'No verified identity, no first day.',
  accounting:     'Bank feed in. Reconciled ledger out. VAT live.',
  terminations:   'Breach letter. Locked portal. Every step on record.',
  'whatsapp-agent': 'Every WhatsApp answered. Calls booked in-thread.',
  'faq-assistant':  'Asked at 2am. Answered at 2am. From your rules.',
  assembled:      'Every part you used tonight. One platform.',
};


// ── What the big screen shows for a scene ───────────────────────────────────
// One answer, used by every surface: the projector window, the mirrored-screen
// backdrop, the presenter console and the nav pad. Keeping it here is what
// stops the projector and the backdrop ever disagreeing about a scene.
//
//   'live'     → the room sees the presenter's REAL admin page for this part
//   'backdrop' → the room sees the deck's scene card, nothing real
//
// A scene is only ever 'live' when someone ticked it AND the part actually has
// an admin page to show. An opted-in scene whose part has no page falls back
// to the backdrop rather than showing the room a blank route.
// A sub_route scene proves itself on a DIFFERENT admin page than its part's
// default (Kit your crew is Operations, but its office half is the Uniforms
// desk, not the allocation board).
const SCENE_ADMIN_OVERRIDES = {
  '/subcontractor/dashboard/uniform': { label: 'Uniforms desk', path: '/admin/dashboard/uniforms' },
  // The training scene is eyes-up (no handset surface), so its sub_route is
  // never navigated by an attendee — it exists purely to prove the scene on
  // the training planner instead of recruitment's default pipeline page.
  '/admin/dashboard/recruitment/training': { label: 'Training planner', path: '/admin/dashboard/recruitment/training' },
};

export const adminLinkForScene = (scene) =>
  (scene?.sub_route && SCENE_ADMIN_OVERRIDES[scene.sub_route])
  || (scene?.part_key && PART_ADMIN_LINKS[scene.part_key]?.[0])
  || null;

export const adminPathForScene = (scene) => adminLinkForScene(scene)?.path || null;

// Admin links may carry a query string (the van lab's ?showreel=1); comparing
// window.location.pathname against the full link would never match and loop
// the navigation. Every "am I already there?" check goes through this.
export const samePath = (pathname, link) =>
  !!link && pathname === String(link).split('?')[0];

// Next decides what the room sees — the presenter never navigates by hand
// (owner, 1 Oct). An EYES-UP scene with a proving page goes live on its own:
// "eyes up" means the room looks at the real thing, not at a card describing
// it. Hands-on scenes stay on the backdrop (the projector rests on the
// curtain there); the per-scene show_live tick remains the manual override
// for revealing the office side of a hands-on scene mid-sentence.
// ── Scene slide image (20261254) ────────────────────────────────────────────
// One answer for every surface (takeover, projector card, designer preview):
// the admin-chosen image_path wins; otherwise the stored capture for the
// scene's page (sub_route's last segment, else the part key — the library in
// /demo-previews is keyed that way). Renderers hide the frame on load error,
// so a part with no capture simply shows no image.
export const sceneImage = (scene) => {
  const autoKey = scene?.sub_route
    ? scene.sub_route.split('/').filter(Boolean).pop()
    : scene?.part_key;
  const src = scene?.image_path || (autoKey ? `/demo-previews/${autoKey}.png` : null);
  return {
    src,
    motion: scene?.image_motion || 'float',
    frame: scene?.image_frame || 'device',
  };
};

// ── Slide theme (20261255) ──────────────────────────────────────────────────
// Dark (default) or light background per scene, admin-chosen. One token set
// so the takeover, both scene cards and the designer preview flip contrast
// identically — no surface picks its own greys.
export const sceneTheme = (scene) => {
  const light = scene?.slide_theme === 'light';
  return light ? {
    light: true,
    stage: 'bg-slate-50',
    panel: 'bg-slate-50/[0.98] text-slate-900',
    heading: 'text-slate-900',
    body: 'text-slate-600',
    bullet: 'text-slate-700',
    faint: 'text-slate-400',
    chip: 'border-amber-500/50 bg-amber-500/10 text-amber-700',
    frame: 'border-slate-900/15',
    dot: 'bg-slate-300',
    ghostBtn: 'border-slate-300 bg-white text-slate-700',
    close: 'border-slate-300 text-slate-500 hover:text-slate-900',
  } : {
    light: false,
    stage: 'bg-slate-950',
    panel: 'bg-slate-950/95 text-white',
    heading: 'text-white',
    body: 'text-slate-300',
    bullet: 'text-slate-200',
    faint: 'text-slate-500',
    chip: 'border-amber-400/40 bg-amber-400/10 text-amber-300',
    frame: 'border-white/15',
    dot: 'bg-white/20',
    ghostBtn: 'border-white/15 bg-white/5 text-slate-200',
    close: 'border-white/10 text-slate-400 hover:text-white',
  };
};

export const sceneStage = (scene) =>
  ((scene?.show_live || isEyesUpScene(scene)) && adminPathForScene(scene)) ? 'live' : 'backdrop';

// A scene where attendees should watch the projector, not their phone. The
// authored decks signal it in the hint ("Eyes up…", "Watch the big screen");
// parts with no subcontractor surface are eyes-up by definition.
export const isEyesUpScene = (scene) => {
  if (!scene) return false;
  if (/eyes up|big screen/i.test(scene.hint || '')) return true;
  return !PART_SUB_ROUTES[scene.part_key];
};

// ── Scene lock: the presentation drives the attendee's navigation ────────────
// The demo phone only opens parts already presented (cumulative, like the van
// assembling), plus anything the presenter opened early; the closing
// 'assembled' scene (or access_mode 'free') opens the whole platform. Core
// Portal is the chassis — never locked. Enforcement is stagecraft for is_demo
// identities only; RLS remains the real boundary.
export const ALL_PART_KEYS = Object.keys(PART_META);

// Subcontractor routes that BELONG to a lockable part. Anything not listed
// (dashboard home, messages, routing, profile…) is Core Portal surface and
// stays open — the guard fails open by construction.
export const ROUTE_PART = {
  '/subcontractor/dashboard/availability': 'operations',
  '/subcontractor/dashboard/uniform': 'operations',
  '/subcontractor/dashboard/logs': 'routes-pay',
  '/subcontractor/dashboard/live-invoice': 'invoicing',
  '/subcontractor/dashboard/performance': 'performance',
  '/subcontractor/dashboard/my-day': 'my-day',
  '/subcontractor/dashboard/field-assist': 'field-assist',
  '/subcontractor/dashboard/cases': 'field-cases',
};

export const partForRoute = (pathname) => {
  const hit = Object.keys(ROUTE_PART).find(
    (r) => pathname === r || (pathname || '').startsWith(`${r}/`),
  );
  return hit ? ROUTE_PART[hit] : null;
};

/**
 * The set of part keys open on attendee devices right now. Fail-open: any
 * missing input opens everything — a frozen phone in front of a buyer costs
 * more than a lost reveal.
 */
export function unlockedPartsAt(scenes, sceneIdx, openedParts = [], accessMode = 'script') {
  const all = new Set(ALL_PART_KEYS);
  if (accessMode === 'free') return all;
  if (!Array.isArray(scenes) || scenes.length === 0 || sceneIdx == null || sceneIdx < 0) return all;
  const open = new Set(['core-portal']);
  for (let i = 0; i <= Math.min(sceneIdx, scenes.length - 1); i += 1) {
    const pk = scenes[i]?.part_key;
    if (pk === 'assembled') return all;
    if (pk && PART_META[pk]) open.add(pk);
  }
  (Array.isArray(openedParts) ? openedParts : []).forEach((pk) => {
    if (PART_META[pk]) open.add(pk);
  });
  return open;
}

// Owner direction (1 Oct): PROBLEM-FIRST sales deck. title = what actually
// happens in a multicrew contractor business today (disconnected services,
// manual admin, industry language); punchline = the solution hammer on the
// takeover; hint = one "Today: …" line + the action + pin-point '•' solution
// bullets. "Eyes up"/"big screen" in a hint is load-bearing (it classifies
// the scene eyes-up). Seeded to demo_scene_defs by 20261251; this array is
// the verbatim fallback.
export const DEMO_SCENES = [
  {
    part_key: 'core-portal',
    title: 'Ten logins, one WhatsApp group and a filing cabinet',
    punchline: 'Every crew, contract and message. One system.',
    hint: "Today a multicrew business runs on scattered apps and paper. You're signed in to the real product — one login replaces the lot.\n• Crew details and contracts stored once — never re-asked\n• Messages the office can prove were read\n• Any phone's browser — nothing to install",
    notes: 'Intro. Every attendee is a subcontractor on an isolated mirror of a real crew. Frame it as a PART from the first minute: the Core Portal (Chassis, £12) — directory, messaging, secure logins — is what everything else bolts onto.',
  },
  {
    part_key: 'operations',
    title: "Who's in Friday? Chased name by name",
    punchline: 'The week submitted in seconds. Nobody chases.',
    hint: "Today: availability lives in a WhatsApp thread someone retypes. Open Availability. Tap your days, Submit.\n• One submit covers the whole week\n• Clone this week into next in one tap\n• A cancellation becomes a request — not a 6am surprise",
    notes: 'Operations Engine (Engine, £8), crew side: multi-week planner, week cloning, one submit — replaces the WhatsApp "who\'s in Friday?" thread. Watch the room submit live.',
  },
  {
    part_key: 'operations',
    title: 'The rota, retyped from chats into a spreadsheet',
    punchline: 'From their phones to the board. Untouched.',
    hint: "Eyes up — the availability the room just sent is already on the office board.\n• Nothing copied from chats into spreadsheets\n• Double-bookings refused by the board itself\n• Drop-outs trigger cover offers automatically",
    notes: 'Operations Engine, office side: the board, batch allocate, conflict guards — it will not double-book. Allocate one attendee live so they see their phone update. Same part, both ends.',
  },
  {
    part_key: 'operations',
    title: 'Sizes collected by text, every single season',
    punchline: 'Kit requested, compiled, capped. No thread.',
    hint: "Today: a sizes thread, a retyped list, an over-order. Open Uniform. Pick an item, choose a size, send.\n• Every request lands in one depot order\n• Per-depot limits stop over-ordering\n• The office previews before anything sends",
    notes: 'Operations Engine, still: crew kit without the WhatsApp sizes thread. Attendees request live; show the admin Uniforms desk compiling one depot email from the room\'s requests — PREVIEW it, do not send. Demo requests never reach the real compile list.',
    sub_route: '/subcontractor/dashboard/uniform',
  },
  {
    part_key: 'routes-pay',
    title: "Last night's routes, typed up all over again",
    punchline: "The route file becomes the day's hours. Nobody types.",
    hint: "Today: hours re-keyed every evening from screenshots. Open Route Logs — today's routes arrived on their own.\n• Hours land on whoever actually worked the day\n• Nothing unclear is guessed — it queues for a human\n• The evening admin shift is gone",
    notes: 'Routes → Pay (Drivetrain, £5). The USP moment: route file lands → hours sync to the allocated crew → the log exists. Unmatched routes queue for a human — it never invents hours.',
  },
  {
    part_key: 'invoicing',
    title: 'Friday: the day invoices eat',
    punchline: 'Routes in. Finished invoice out.',
    hint: "Today: the week rebuilt from messages, line by line. Open Invoices — this week is already assembled.\n• Every line opens the route log behind it\n• Disputes die in seconds, not email chains\n• Query one line without holding up the rest",
    notes: 'Invoicing Engine (Fuel gauge, £6): route logs → invoice lines → weekly invoice, with rate schedules incl. OTR/retraining/trainee. Every line traces to a route log — disputes die in seconds. The part nobody else sells.',
  },
  {
    part_key: 'performance',
    title: 'Carrier scorecards that die in an inbox',
    punchline: 'Grading emails become standings and coaching.',
    hint: "Today: the carrier grades you every week and nobody actions it. Open your Performance hub.\n• Scores parsed automatically — never filed and forgotten\n• Credit follows whoever worked the route\n• Five-star awards tracked to the exact day",
    notes: "Performance Intelligence (Dashboard, £5): the carrier's own emails parsed into gradings, leaderboards and coaching automatically. Attribution follows who actually worked the day.",
  },
  {
    part_key: 'recruitment',
    title: 'Onboarding run on email threads and folders',
    punchline: 'No verified identity, no first day.',
    hint: "Eyes up — the candidate pipeline on the big screen. Today: documents chased by hand, starts booked on trust.\n• The system chases documents, not the office\n• An unverified driver cannot reach day one\n• Approved candidates sent straight into trainee onboarding",
    notes: 'Recruitment & Onboarding (Crew seats, £5): one pipeline from application to booked start. It cannot seat an unverified driver — no ID, no start. Approved candidates are SENT into trainee onboarding from here — same part, next scene shows where they land. Show the journey rail and the stage machine.',
  },
  {
    part_key: 'recruitment',
    title: 'Training juggled in calendars, trainers paid on memory',
    punchline: 'Booked, trained, graduated. One planner.',
    hint: "Eyes up — the training planner on the big screen. Today: Day 1s, assessments and trainer pay live in three different places.\n• Day 1, OTR days and assessments on one grid\n• Trainer and trainee pay follow the booking automatically\n• The depot file counts trainees straight from the plan",
    notes: "Training planner, inside Recruitment & Onboarding (same part — the pipeline's approved candidates land here). Trainer pay, learner rates and the depot file all read from the plan, so nothing is paid or reported from memory. Like My Day, this packages as an embeddable SDK for buyers running their own academy front-end.",
    sub_route: '/admin/dashboard/recruitment/training',
  },
  {
    part_key: 'terminations',
    title: 'Exits that end in disputes and missing kit',
    punchline: 'Breach letter. Locked portal. Every step on record.',
    hint: "Eyes up — the termination desk on the big screen. Today: exits are a text message and a hope the kit comes back.\n• Breach letter cited from the signed agreement\n• Portal locks to a bounded archive window\n• Watch your inbox — a demonstration letter is on its way to you",
    notes: "Terminations Desk (Handbrake): breach letter cited from the signed agreement, portal locked to a bounded archive window, full deactivation on expiry — every step on the record. Press LETTERS on the pad to send every attendee a watermarked demonstration letter to the email they signed with (nothing touches their demo access). Mind the room: the desk shows real personnel names.",
  },
  {
    part_key: 'accounting',
    title: 'Year-end from a shoebox of statements',
    punchline: 'Bank feed in. Reconciled ledger out. VAT live.',
    hint: "Eyes up — live bank feeds and a reconciled ledger on the big screen. Today: receipts in folders, VAT a quarterly shock.\n• Categorisation learns your operation\n• VAT and CT live all year — no surprises\n• The accountant gets access, not a box of paperwork",
    notes: 'Accounting Bridge (Logbook, £6, premium): bank feed → learned categorisation → reconciled ledger → live VAT/CT. Locked periods; accountant-ready exports. Depends on Invoicing — sell them together.',
  },
  {
    title: 'Every part is also a product',
    punchline: 'Run it. Resell it.',
    slide_theme: 'light',
    hint: "Eyes up \u2014 the pivot. Everything so far runs your own operation.\n\u2022 Everything from here is also sold on its own\n\u2022 Priced per crew, plugged into platforms you already run\n\u2022 Your clients buy the piece they need \u2014 you keep the margin",
    notes: 'The act divider: Act 1 was the portal running YOUR operation; everything after this slide is also a standalone product your clients can buy per crew. Let the room feel the shift \u2014 pause here.',
  },
  {
    part_key: 'my-day',
    title: 'Working the morning backwards from a start time',
    punchline: 'Route start in. Wake, leave, arrive out.',
    hint: "Today: every driver does the maths the night before. Open My Day — home and depot go in once.\n• The wake alarm escalates until it is answered\n• Your own calendar fills itself\n• Server backup push if the phone sleeps through it",
    notes: 'My Day (Sat-nav, £4): route start → the whole morning, with escalating wake alarm, server backup push and a live calendar feed. Also our first packaged SDK — this part embeds in a buyer\'s own app.',
  },
  {
    part_key: 'field-assist',
    title: 'Doorstep guesswork that turns into damage claims',
    punchline: 'Point the phone. Governed answers, spoken back.',
    hint: "Today: the driver guesses, the claim arrives later. Open Field Assist and ask about the appliance in front of you.\n• Proceed or stop decided by your rules, not memory\n• Answers spoken aloud — hands stay free\n• Crews earn per captured appliance on the research panel",
    notes: 'Field Assist (Toolbox, £4): governed doorstep intelligence — proceed / do-not-proceed is decided by the backend, never guessed by the phone. Mention the paid research panel: crews earn per captured appliance.',
  },
  {
    part_key: 'field-cases',
    title: 'PCNs paid because challenging was a chore',
    punchline: 'Evidence filed at the kerb. Replies reviewed.',
    hint: "Today: fines paid, photos lost, replies sent unreviewed. Open Cases.\n• Photos and paperwork filed the moment it happens\n• Deadlines tracked — no fine forgotten into the higher rate\n• One challenged PCN pays for a crew's year",
    notes: "Field Cases (Glovebox, £4): PCNs, damage and complaints with evidence, deadlines and reviewed correspondence. One challenged PCN pays a crew's year. Nothing sends unreviewed.",
  },
  {
    part_key: 'whatsapp-agent',
    title: 'Leads and drivers messaging a phone nobody owns',
    punchline: 'Every WhatsApp answered. Calls booked in-thread.',
    slide_theme: 'light',
    hint: "Eyes up \u2014 the live WhatsApp desk on the big screen. Today: enquiries and rescues wait for whoever checks the phone next.\n\u2022 Inbound matched to candidate, crew or prospect automatically\n\u2022 Rescue sequences and call bookings handled in the thread\n\u2022 Unmatched messages mirrored straight to the owner's phone",
    notes: 'WhatsApp Agent (Two-way radio, \u00a36): the number answers itself \u2014 rescue sequences, B2B call booking, FAQ replies, owner alerts for anything unmatched. Show the live inbox and open ONE chosen thread. PRIVACY: threads carry real names and numbers \u2014 pick the thread before the room sees the list; the stage mask covers names it knows, not phone numbers.',
  },
  {
    part_key: 'faq-assistant',
    title: 'The same ten questions, answered one by one',
    punchline: 'Asked at 2am. Answered at 2am. From your rules.',
    slide_theme: 'light',
    hint: "Eyes up \u2014 watch a question answered live. Today: every new starter asks the same ten things, and someone types the answers again.\n\u2022 Answers come only from your approved rules \u2014 never guessed\n\u2022 Out-of-hours questions answered the moment they arrive\n\u2022 Anything unknown goes to a human instead of an invention",
    notes: "FAQ Assistant (Owner's manual, \u00a33): the assistant's FAQ brain as its own product \u2014 grounded in rules you approve, refuses rather than invents. Prove it live: text the session number a question from your own phone and let the room watch the reply land in the inbox on screen.",
  },
  {
    part_key: 'numbers',
    title: 'What it does to your numbers',
    punchline: 'Priced per crew. Paid back in admin days.',
    hint: "Eyes up — the forecast for an operation your size.\n• Priced per crew, per month, part by part\n• Pays for itself in recovered admin days\n• We run the scenario that matches your base",
    notes: 'Tenanted forecast: per-crew pricing tiers, a 30-crew operator ≈ £12.6k/yr, unit economics (LTV ≈ £36k, payback ≈ 3 months), ARR scenarios incl. the partner channel. Run the scenarios that match THEIR base.',
  },
  {
    part_key: 'assembled',
    title: 'Your platform, assembled',
    punchline: 'Every part you used tonight. One platform.',
    hint: "Watch the van assemble on the big screen — every part you used tonight, in one build.\n• Buy only the parts you need\n• Live in days — not a software project\n• Your branding, your data fully isolated",
    notes: 'Close. Every scene tonight was one part; here is the van they assemble into. Parts model, white-label, per-tenant isolated databases, automated provisioning from subscription. The ask: co-branded pilot with firms from their base. End session (purges every demo identity).',
  },
];

const fallbackScenes = () =>
  DEMO_SCENES.map((s) => ({ id: null, title: s.title, hint: s.hint, notes: s.notes ?? null, demo_key: null, part_key: s.part_key ?? null, show_live: false, sub_route: s.sub_route ?? null, punchline: s.punchline ?? null, image_path: null, image_motion: null, image_frame: null, slide_theme: s.slide_theme ?? null }));

/**
 * The live deck: active demo_scene_defs in position order, shaped as
 * { id, title, hint, notes, demo_key, part_key, show_live, sub_route }. Falls
 * back to the built-in DEMO_SCENES on error or when the table is empty, so the
 * deck always exists. Callers cache per mount — no module cache here.
 */
/**
 * Tick / untick "the room sees my real screen" for one scene.
 *
 * Writes straight to demo_scene_defs (admin RLS), so the change reaches the
 * projector and the mirrored backdrop on their next poll — a presenter can
 * open a screen up mid-sentence when the room asks to see something, and shut
 * it again afterwards.
 *
 * Fallback scenes have no id (the deck is the built-in array because the table
 * is empty), and there is nothing to write to — callers disable the control.
 */
export async function setSceneShowLive(sceneId, on) {
  if (!sceneId) throw new Error('This deck is the built-in fallback — add scenes in Product Demos first.');
  const { error } = await supabase
    .from('demo_scene_defs')
    .update({ show_live: !!on, updated_at: new Date().toISOString() })
    .eq('id', sceneId);
  if (error) throw new Error(error.message);
  return !!on;
}

const SCENE_COLUMNS = 'id, title, hint, notes, demo_key, part_key';

const shapeScene = (r) => ({
  id: r.id,
  title: r.title,
  hint: r.hint,
  notes: r.notes ?? null,
  demo_key: r.demo_key || null,
  part_key: r.part_key || null,
  show_live: !!r.show_live,
  sub_route: r.sub_route || null,
  punchline: r.punchline || null,
  image_path: r.image_path || null,
  image_motion: r.image_motion || null,
  image_frame: r.image_frame || null,
  slide_theme: r.slide_theme || null,
});

const selectScenes = (columns) =>
  supabase
    .from('demo_scene_defs')
    .select(columns)
    .eq('active', true)
    .order('position', { ascending: true });

export async function fetchSceneDefs() {
  try {
    // show_live arrived with 20261222, sub_route with 20261223 and punchline
    // with 20261251; selecting a column the database does not have yet makes
    // PostgREST refuse the WHOLE read. Falling straight through to the
    // built-in deck would silently replace the admin's AUTHORED scenes with
    // the fallback array — mid-presentation, with a room watching. So degrade
    // column by column: each missing column loses only its own feature,
    // never the deck.
    let { data, error } = await selectScenes(`${SCENE_COLUMNS}, show_live, sub_route, punchline, image_path, image_motion, image_frame, slide_theme`);
    if (error) ({ data, error } = await selectScenes(`${SCENE_COLUMNS}, show_live, sub_route, punchline, image_path, image_motion, image_frame`));
    if (error) ({ data, error } = await selectScenes(`${SCENE_COLUMNS}, show_live, sub_route, punchline`));
    if (error) ({ data, error } = await selectScenes(`${SCENE_COLUMNS}, show_live, sub_route`));
    if (error) ({ data, error } = await selectScenes(`${SCENE_COLUMNS}, show_live`));
    if (error) ({ data, error } = await selectScenes(SCENE_COLUMNS));
    if (error || !Array.isArray(data) || data.length === 0) return fallbackScenes();
    return data.map(shapeScene);
  } catch {
    return fallbackScenes();
  }
}
